import React, {createContext, useContext, useState, useRef, useMemo, useEffect} from 'react'
import {useStickyState} from './sticky-context'

const HeaderContext = createContext()

export const HeaderProvider = ({children}) => {
    const lastScrollY = useRef(0)
    const [shouldShowSticky, setShouldShowSticky] = useState(false)
    const [headerHeight, setHeaderHeight] = useState(0)
    const headerRef = useRef(null)
    const {pdpStickyButtonActive} = useStickyState()
    const canHideHeader = useRef(false)
    const [shouldShowHeader, setShouldShowHeader] = useState(true)
    const [category, setCategory] = useState(null)
    const [product, setProduct] = useState(null)
    const [pageType, setPageType] = useState(null)

    // Header variant state: '1' (transparent white), '2' (transparent black), '3' (solid white)
    const [headerVariant, setHeaderVariant] = useState('3')
    // Gradient and drop shadow config (set by HeaderVariantSetter from env vars)
    const [headerGradient, setHeaderGradient] = useState('none')
    const [headerDropShadow, setHeaderDropShadow] = useState(false)
    // Scroll state shared across header and related components
    const [isScrolled, setIsScrolled] = useState(false)
    // Hover zone for transparent header: 'none' | 'nav' | 'icons' | 'gap'
    const [headerHoverZone, setHeaderHoverZone] = useState('none')

    // The header only slides away while a sticky bar (PDP add-to-cart, PLP filters)
    // takes over the top of the viewport. Kept in a ref so the scroll listener below
    // never reads a stale value.
    useEffect(() => {
        const isHideable = pdpStickyButtonActive || shouldShowSticky
        canHideHeader.current = isHideable
        if (!isHideable) {
            setShouldShowHeader(true)
        }
    }, [pdpStickyButtonActive, shouldShowSticky])

    useEffect(() => {
        const handleScroll = () => {
            const currentY = window.scrollY

            // Always update isScrolled so transparent header restores at top
            setIsScrolled(currentY > 0)

            const delta = currentY - lastScrollY.current
            if (Math.abs(delta) < 5) return

            lastScrollY.current = currentY

            setShouldShowHeader(delta < 0 || currentY === 0 || !canHideHeader.current)
        }

        window.addEventListener('scroll', handleScroll, {passive: true})
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const contextValue = useMemo(
        () => ({
            headerRef,
            shouldShowSticky,
            setShouldShowSticky,
            headerHeight,
            setHeaderHeight,
            shouldShowHeader,
            setShouldShowHeader,
            category,
            setCategory,
            product,
            setProduct,
            pageType,
            setPageType,
            headerVariant,
            setHeaderVariant,
            headerGradient,
            setHeaderGradient,
            headerDropShadow,
            setHeaderDropShadow,
            isScrolled,
            headerHoverZone,
            setHeaderHoverZone
        }),
        [
            shouldShowSticky,
            headerHeight,
            shouldShowHeader,
            category,
            product,
            pageType,
            headerVariant,
            headerGradient,
            headerDropShadow,
            isScrolled,
            headerHoverZone
        ]
    )

    return <HeaderContext.Provider value={contextValue}>{children}</HeaderContext.Provider>
}

export const useHeaderContext = () => {
    const context = useContext(HeaderContext)
    if (!context) {
        throw new Error('useHeaderContext must be used within HeaderProvider')
    }
    return context
}
